import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import QRCode from 'react-qr-code';
import * as XLSX from 'xlsx';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { 
  getStaffUsers, saveStaffUsers, addStaffUser, updateStaffUser, deleteStaffUser,
  getSettings, getAllEntries 
} from '../utils/db';
import { playTickSound, playCorrectChime } from '../utils/soundEffects';

const { 
  FiUsers, FiUserCheck, FiPlus, FiTrash2, FiEdit2, FiExternalLink, 
  FiCopy, FiCheck, FiX, FiShield, FiSmartphone, FiMonitor, FiRadio, 
  FiDownload, FiFilter, FiSearch, FiSliders, FiClock, FiActivity,
  FiAward, FiGift, FiTarget, FiLayers, FiColumns, FiHelpCircle, FiKey
} = FiIcons;

// 8 Core Physical Event Stations + Arena Stations
export const EVENT_STATIONS = [
  {
    id: 'scratch',
    stationNumber: 'Station #1',
    name: 'Lucky Scratch Cards Booth',
    route: '/scratch',
    icon: '🎁',
    category: 'Instant Win Card',
    recommendedStaff: '1 Verifier + 1 Host',
    description: 'Visitors scratch foil cards on kiosk touchscreens or tablets.'
  },
  {
    id: 'spin',
    stationNumber: 'Station #2',
    name: 'Mega Spin Wheel Arena',
    route: '/spin',
    icon: '🎯',
    category: 'High-Energy Carnival Wheel',
    recommendedStaff: '1 Spin Master',
    description: 'High-speed physical wheel with ticker sound & dynamic reward sectors.'
  },
  {
    id: 'slot',
    stationNumber: 'Station #3',
    name: 'Vegas 3-Reel Slot Machine',
    route: '/slot',
    icon: '🎰',
    category: 'Casino Stage Kiosk',
    recommendedStaff: '1 Game Master',
    description: 'Pull-lever animated reels with 777 jackpots & token validation.'
  },
  {
    id: 'peel',
    stationNumber: 'Station #4',
    name: 'Peel to Reveal Station',
    route: '/peel',
    icon: '📑',
    category: 'Physical Card Emulation',
    recommendedStaff: '1 Station Host',
    description: 'Corner peel-back card revealing instant surprise vouchers.'
  },
  {
    id: 'flip',
    stationNumber: 'Station #5',
    name: 'Mystery 3D Flip Card Booth',
    route: '/flip',
    icon: '🃏',
    category: 'Interactive Reveal',
    recommendedStaff: '1 Booth Host',
    description: 'Tactile 3D cards that flip upon user tap with sound fanfare.'
  },
  {
    id: 'balloon',
    stationNumber: 'Station #6',
    name: 'Carnival Balloon Pop Kiosk',
    route: '/balloon',
    icon: '🎈',
    category: 'Carnival Skill Game',
    recommendedStaff: '1 Carnival Attendant',
    description: 'Darts pop floating helium balloons to reveal hidden tokens.'
  },
  {
    id: 'pinata',
    stationNumber: 'Station #7',
    name: 'Fiesta Pinata Smash Station',
    route: '/pinata',
    icon: '🪅',
    category: 'Interactive Party Game',
    recommendedStaff: '1 Game Master',
    description: 'Smash stick hits candy pinata until celebration token drops.'
  },
  {
    id: 'treasure',
    stationNumber: 'Station #8',
    name: 'Mystery Golden Treasure Box',
    route: '/treasure',
    icon: '📦',
    category: 'Chest Unlock Game',
    recommendedStaff: '1 Key Master',
    description: 'Visitors pick from 3 golden padlocked chests to unlock mega tokens.'
  },
  {
    id: 'house_barometer',
    stationNumber: 'Stadium Arena',
    name: 'School Sports House Barometer',
    route: '/house-barometer',
    icon: '🏆',
    category: 'Stage Projector Arena',
    recommendedStaff: 'Chief Sports Referee',
    description: 'Large auditorium projector displaying live inter-house rankings & medals.'
  },
  {
    id: 'live_quiz',
    stationNumber: 'Academic Kiosk',
    name: 'Live Stage Quiz & Trivia Arena',
    route: '/live-quiz',
    icon: '🧠',
    category: 'Multiplayer Stage',
    recommendedStaff: 'Quiz Master',
    description: 'Timed questions with buzzer sound and live leaderboards.'
  }
];

const StationRoutingManager = () => {
  const [activeSubTab, setActiveSubTab] = useState('stations'); // 'stations' | 'staff'
  const [staffList, setStaffList] = useState(getStaffUsers());
  const [entries, setEntries] = useState(getAllEntries());
  const [settings, setSettings] = useState(getSettings());
  const [copiedStationId, setCopiedStationId] = useState(null);

  // Filter States
  const [filterEvent, setFilterEvent] = useState('all');
  const [filterStation, setFilterStation] = useState('all');
  const [searchStaff, setSearchStaff] = useState('');

  // Add / Edit Staff Modal State
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [editingStaffId, setEditingStaffId] = useState(null);
  const [staffForm, setStaffForm] = useState({
    name: '',
    role: 'Station Game Master',
    stationId: 'spin',
    assignedEvent: 'Annual Carnival & Sports Gala 2026',
    pinCode: '1234',
    phone: '',
    status: 'on_duty',
    boothLocation: '',
    notes: ''
  });

  const baseUrl = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : '';

  useEffect(() => {
    const handleUpdate = () => {
      setStaffList(getStaffUsers());
      setEntries(getAllEntries());
      setSettings(getSettings());
    };
    window.addEventListener('staffUsersUpdated', handleUpdate);
    window.addEventListener('settingsUpdated', handleUpdate);
    window.addEventListener('storage', handleUpdate);
    return () => {
      window.removeEventListener('staffUsersUpdated', handleUpdate);
      window.removeEventListener('settingsUpdated', handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, []);

  const copyStationLink = (route, id) => {
    const fullUrl = `${baseUrl}#${route}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedStationId(id);
    playTickSound(880, 0.04);
    setTimeout(() => setCopiedStationId(null), 2500);
  };

  const handleOpenAddStaff = () => {
    setEditingStaffId(null);
    setStaffForm({
      name: '',
      role: 'Station Game Master',
      stationId: 'spin',
      assignedEvent: 'Annual Carnival & Sports Gala 2026',
      pinCode: Math.floor(1000 + Math.random() * 9000).toString(),
      phone: '',
      status: 'on_duty',
      boothLocation: 'Booth Station #2',
      notes: ''
    });
    setIsStaffModalOpen(true);
  };

  const handleOpenEditStaff = (staff) => {
    setEditingStaffId(staff.id);
    setStaffForm({ ...staff });
    setIsStaffModalOpen(true);
  };

  const handleSaveStaff = (e) => {
    e?.preventDefault();
    if (!staffForm.name.trim()) return alert("Staff member name is required.");

    const stationObj = EVENT_STATIONS.find(s => s.id === staffForm.stationId);
    const stationName = stationObj ? `${stationObj.stationNumber} - ${stationObj.name}` : staffForm.stationId;

    if (editingStaffId) {
      updateStaffUser(editingStaffId, {
        ...staffForm,
        stationName
      });
      playCorrectChime();
    } else {
      const newStaff = {
        id: `staff_${Date.now()}`,
        ...staffForm,
        stationName
      };
      addStaffUser(newStaff);
      playCorrectChime();
    }

    setStaffList(getStaffUsers());
    setIsStaffModalOpen(false);
  };

  const handleDeleteStaff = (id, name) => {
    if (window.confirm(`Are you sure you want to remove staff member "${name}"?`)) {
      deleteStaffUser(id);
      setStaffList(getStaffUsers());
      playTickSound(500, 0.05);
    }
  };

  const handleToggleStatus = (id, currentStatus) => {
    const nextStatus = currentStatus === 'on_duty' ? 'break' : currentStatus === 'break' ? 'off_duty' : 'on_duty';
    updateStaffUser(id, { status: nextStatus });
    setStaffList(getStaffUsers());
    playTickSound(700, 0.04);
  };

  // Export Staff Roster to Excel
  const handleExportRoster = () => {
    try {
      const rows = staffList.map((s, idx) => ({
        "No.": idx + 1,
        "Staff Name": s.name,
        "Role": s.role,
        "Assigned Station": s.stationName || s.stationId,
        "Assigned Event": s.assignedEvent,
        "Booth Location": s.boothLocation || 'General Booth',
        "Duty Status": s.status === 'on_duty' ? 'ON DUTY' : s.status === 'break' ? 'ON BREAK' : 'OFF DUTY',
        "Phone / Contact": s.phone || 'N/A',
        "Station PIN": s.pinCode,
        "Notes": s.notes || ''
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, "Staff Station Allocations");
      XLSX.writeFile(wb, `event_staff_station_roster_${new Date().toISOString().split('T')[0]}.xlsx`);
      playTickSound(880, 0.05);
    } catch (e) {
      alert("Failed to export staff roster: " + e.message);
    }
  };

  // Filter staff list
  const filteredStaff = staffList.filter(s => {
    const matchEvent = filterEvent === 'all' || s.assignedEvent === filterEvent;
    const matchStation = filterStation === 'all' || s.stationId === filterStation;
    const matchSearch = String(s.name || '').toLowerCase().includes(searchStaff.toLowerCase()) ||
                        String(s.role || '').toLowerCase().includes(searchStaff.toLowerCase());
    return matchEvent && matchStation && matchSearch;
  });

  const uniqueEvents = Array.from(new Set(staffList.map(s => s.assignedEvent).filter(Boolean)));

  return (
    <div className="space-y-6">

      {/* HUB HEADER & DUAL-TAB NAVIGATION */}
      <div className="p-5 md:p-6 rounded-2xl bg-gradient-to-r from-blue-600/10 via-indigo-600/10 to-purple-600/10 border border-blue-500/20 backdrop-blur-md shadow-sm">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-700 dark:text-blue-300 text-xs font-black uppercase flex items-center gap-1.5 shadow-sm">
                <SafeIcon icon={FiRadio} /> Live Operations Hub
              </span>
              <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">
                Physical Kiosks & Staff Allocation
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight mt-1">
              🎪 Live Mode & Station Routing Center
            </h2>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 max-w-2xl">
              Route physical game stations to event booths and assign staff operators station-wise & school event-wise for streamlined crowd flow.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <button
              type="button"
              onClick={() => setActiveSubTab('stations')}
              className={`px-4 py-2.5 rounded-xl text-xs font-black transition-all flex items-center gap-2 cursor-pointer shadow-sm ${
                activeSubTab === 'stations'
                  ? 'bg-blue-600 text-white shadow-blue-500/25 ring-2 ring-blue-500/30'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              <span>🎪</span>
              <span>Event Stations ({EVENT_STATIONS.length})</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveSubTab('staff')}
              className={`px-4 py-2.5 rounded-xl text-xs font-black transition-all flex items-center gap-2 cursor-pointer shadow-sm ${
                activeSubTab === 'staff'
                  ? 'bg-indigo-600 text-white shadow-indigo-500/25 ring-2 ring-indigo-500/30'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              <SafeIcon icon={FiUsers} />
              <span>Staff Allocations ({staffList.length})</span>
            </button>

            <button
              type="button"
              onClick={handleExportRoster}
              className="px-3.5 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold transition-colors flex items-center gap-1.5 shadow-sm cursor-pointer"
              title="Export staff roster to Excel"
            >
              <SafeIcon icon={FiDownload} />
              <span>Export Roster (.xlsx)</span>
            </button>
          </div>
        </div>
      </div>

      {/* VIEW 1: EVENT STATIONS & LIVE KIOSK ROUTING GRID */}
      {activeSubTab === 'stations' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-slate-800/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-700/80">
            <div>
              <h3 className="text-sm font-black text-slate-900 dark:text-white flex items-center gap-2">
                <span>📍 Active Event Station Terminals</span>
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Each physical station runs independently on a tablet or touchscreen kiosk. Visitors scan the QR code to play at that specific station.
              </p>
            </div>
            <button
              type="button"
              onClick={handleOpenAddStaff}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
            >
              <SafeIcon icon={FiPlus} />
              <span>Assign Staff to Station</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {EVENT_STATIONS.map((station) => {
              const fullUrl = `${baseUrl}#${station.route}`;
              const assignedStaff = staffList.filter(s => s.stationId === station.id);
              const isCopied = copiedStationId === station.id;

              return (
                <div 
                  key={station.id}
                  className="glass-panel p-5 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/70 shadow-sm flex flex-col justify-between hover:shadow-md transition-all"
                >
                  <div className="space-y-3">
                    {/* Station Badge Header */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2.5">
                        <span className="text-2xl p-2 rounded-xl bg-slate-100 dark:bg-slate-700/60 shadow-inner flex-shrink-0">
                          {station.icon}
                        </span>
                        <div>
                          <span className="text-[10px] font-black uppercase text-blue-600 dark:text-blue-400 tracking-wider">
                            {station.stationNumber}
                          </span>
                          <h4 className="text-xs font-bold text-slate-900 dark:text-white leading-tight">
                            {station.name}
                          </h4>
                        </div>
                      </div>
                      <span className="text-[9px] px-2 py-0.5 rounded-full font-black uppercase tracking-wider bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" /> Live
                      </span>
                    </div>

                    <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2 leading-tight">
                      {station.description}
                    </p>

                    {/* QR Code and Station Link */}
                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-700/60 flex items-center gap-3">
                      <div className="bg-white p-1 rounded-lg border border-slate-200 shadow-sm flex-shrink-0">
                        <QRCode value={fullUrl} size={54} fgColor="#0f172a" level="L" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-[10px] text-slate-400 uppercase font-black tracking-wider block">Terminal URL</span>
                        <div className="text-[11px] font-mono font-bold text-slate-700 dark:text-slate-300 truncate">
                          #{station.route}
                        </div>
                        <div className="text-[10px] text-blue-600 dark:text-blue-400 font-semibold mt-0.5">
                          {station.category}
                        </div>
                      </div>
                    </div>

                    {/* Assigned Staff Status Badge */}
                    <div className="pt-1">
                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block mb-1">
                        Assigned Operator ({assignedStaff.length}):
                      </span>
                      {assignedStaff.length > 0 ? (
                        <div className="space-y-1">
                          {assignedStaff.map(s => (
                            <div key={s.id} className="flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600/50">
                              <div className="flex items-center gap-1.5 truncate">
                                <span className={`w-2 h-2 rounded-full ${s.status === 'on_duty' ? 'bg-emerald-500' : s.status === 'break' ? 'bg-amber-500' : 'bg-slate-400'}`} />
                                <span className="font-bold text-slate-800 dark:text-slate-200 truncate">{s.name}</span>
                              </div>
                              <span className="text-[10px] font-mono text-slate-400 ml-1">PIN: {s.pinCode}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-[11px] text-amber-600 dark:text-amber-400 italic bg-amber-50 dark:bg-amber-950/20 px-2 py-1 rounded-lg border border-amber-200 dark:border-amber-800/40">
                          No staff assigned yet
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions Footer */}
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                    <button
                      type="button"
                      onClick={() => copyStationLink(station.route, station.id)}
                      className="flex-1 py-1.5 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <SafeIcon icon={isCopied ? FiCheck : FiCopy} className={isCopied ? "text-emerald-500" : ""} />
                      <span>{isCopied ? "Link Copied!" : "Copy URL"}</span>
                    </button>

                    <a
                      href={fullUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold flex items-center gap-1 shadow-sm transition-colors"
                      title="Open Station in New Window / Kiosk Mode"
                    >
                      <span>Open</span>
                      <SafeIcon icon={FiExternalLink} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* VIEW 2: STAFF USERS ALLOCATION ROSTER */}
      {activeSubTab === 'staff' && (
        <div className="space-y-6">
          {/* Controls Bar */}
          <div className="glass-panel p-4 rounded-2xl bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-2.5 flex-1">
              <div className="relative min-w-[200px] flex-1">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                <input
                  type="text"
                  value={searchStaff}
                  onChange={e => setSearchStaff(e.target.value)}
                  placeholder="Search staff by name or role..."
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl pl-8 pr-3 py-1.5 text-xs text-slate-800 dark:text-white outline-none focus:border-indigo-500"
                />
              </div>

              {/* Filter by Event */}
              <div className="flex items-center gap-1.5">
                <label className="text-[11px] font-bold text-slate-500">Event:</label>
                <select
                  value={filterEvent}
                  onChange={e => setFilterEvent(e.target.value)}
                  className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-1.5 text-xs text-slate-800 dark:text-white font-bold outline-none cursor-pointer"
                >
                  <option value="all">All Events</option>
                  {uniqueEvents.map(ev => (
                    <option key={ev} value={ev}>{ev}</option>
                  ))}
                </select>
              </div>

              {/* Filter by Station */}
              <div className="flex items-center gap-1.5">
                <label className="text-[11px] font-bold text-slate-500">Station:</label>
                <select
                  value={filterStation}
                  onChange={e => setFilterStation(e.target.value)}
                  className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-1.5 text-xs text-slate-800 dark:text-white font-bold outline-none cursor-pointer"
                >
                  <option value="all">All Stations</option>
                  {EVENT_STATIONS.map(st => (
                    <option key={st.id} value={st.id}>{st.stationNumber} ({st.name})</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              type="button"
              onClick={handleOpenAddStaff}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <SafeIcon icon={FiPlus} />
              <span>Add Staff Member</span>
            </button>
          </div>

          {/* Staff Roster Table */}
          <div className="glass-panel rounded-2xl bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-700/80 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="p-3.5">Staff Name & Role</th>
                    <th className="p-3.5">Assigned Station</th>
                    <th className="p-3.5">Assigned Event</th>
                    <th className="p-3.5">Booth Location</th>
                    <th className="p-3.5">Duty Status</th>
                    <th className="p-3.5">Station PIN</th>
                    <th className="p-3.5">Contact</th>
                    <th className="p-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                  {filteredStaff.length > 0 ? (
                    filteredStaff.map((staff) => {
                      const stObj = EVENT_STATIONS.find(s => s.id === staff.stationId);
                      return (
                        <tr key={staff.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/20 transition-colors">
                          <td className="p-3.5">
                            <div className="font-bold text-slate-900 dark:text-white text-xs">{staff.name}</div>
                            <div className="text-[10px] text-indigo-600 dark:text-indigo-400 font-semibold">{staff.role}</div>
                          </td>
                          <td className="p-3.5">
                            <div className="flex items-center gap-1.5 font-bold text-slate-800 dark:text-slate-200">
                              <span>{stObj?.icon || '🎮'}</span>
                              <span>{stObj ? `${stObj.stationNumber} - ${stObj.name}` : staff.stationName}</span>
                            </div>
                          </td>
                          <td className="p-3.5">
                            <span className="px-2 py-0.5 rounded-md bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 font-medium text-[11px] border border-purple-200 dark:border-purple-800/40">
                              {staff.assignedEvent}
                            </span>
                          </td>
                          <td className="p-3.5 text-slate-600 dark:text-slate-400 font-medium">
                            {staff.boothLocation || 'General Arena'}
                          </td>
                          <td className="p-3.5">
                            <button
                              type="button"
                              onClick={() => handleToggleStatus(staff.id, staff.status)}
                              className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border cursor-pointer transition-all flex items-center gap-1.5 ${
                                staff.status === 'on_duty'
                                  ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800'
                                  : staff.status === 'break'
                                  ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-800'
                                  : 'bg-slate-100 dark:bg-slate-800 text-slate-500 border-slate-300 dark:border-slate-700'
                              }`}
                              title="Click to toggle status: On Duty / Break / Off Duty"
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${staff.status === 'on_duty' ? 'bg-emerald-500' : staff.status === 'break' ? 'bg-amber-500' : 'bg-slate-400'}`} />
                              <span>{staff.status === 'on_duty' ? 'ON DUTY' : staff.status === 'break' ? 'BREAK' : 'OFF DUTY'}</span>
                            </button>
                          </td>
                          <td className="p-3.5 font-mono font-bold text-slate-700 dark:text-slate-300">
                            {staff.pinCode}
                          </td>
                          <td className="p-3.5 text-slate-500 dark:text-slate-400">
                            {staff.phone || '—'}
                          </td>
                          <td className="p-3.5 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleOpenEditStaff(staff)}
                                className="p-1.5 rounded-lg text-slate-500 hover:text-indigo-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                                title="Edit Staff Member"
                              >
                                <SafeIcon icon={FiEdit2} className="text-xs" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteStaff(staff.id, staff.name)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors cursor-pointer"
                                title="Remove Staff Member"
                              >
                                <SafeIcon icon={FiTrash2} className="text-xs" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-400">
                        No staff members matching your filter criteria. Click "Add Staff Member" to allocate staff.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ADD / EDIT STAFF MODAL */}
      <AnimatePresence>
        {isStaffModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-3xl p-6 sm:p-7 max-w-lg w-full shadow-2xl relative"
            >
              <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-800">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
                    <SafeIcon icon={FiUsers} className="text-lg" />
                  </div>
                  <div>
                    <h3 className="text-base font-black text-slate-900 dark:text-white">
                      {editingStaffId ? 'Edit Staff Allocation' : 'Assign New Staff Member'}
                    </h3>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">Allocate staff to an event station</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsStaffModalOpen(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>

              <form onSubmit={handleSaveStaff} className="space-y-4 pt-4 text-xs">
                {/* Name & Role */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Staff Full Name *</label>
                    <input
                      type="text"
                      required
                      value={staffForm.name}
                      onChange={e => setStaffForm({ ...staffForm, name: e.target.value })}
                      placeholder="e.g. Rajesh Sharma"
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-900 dark:text-white font-bold outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Operational Role</label>
                    <select
                      value={staffForm.role}
                      onChange={e => setStaffForm({ ...staffForm, role: e.target.value })}
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-900 dark:text-white font-bold outline-none cursor-pointer"
                    >
                      <option value="Station Game Master">Station Game Master</option>
                      <option value="Token Verifier & Gatekeeper">Token Verifier & Gatekeeper</option>
                      <option value="Scorekeeper & Referee">Scorekeeper & Referee</option>
                      <option value="Carnival Host">Carnival Host</option>
                      <option value="Chief Sports Referee">Chief Sports Referee</option>
                      <option value="Arena Coordinator">Arena Coordinator</option>
                    </select>
                  </div>
                </div>

                {/* Assigned Station & Assigned Event */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Assigned Station *</label>
                    <select
                      value={staffForm.stationId}
                      onChange={e => setStaffForm({ ...staffForm, stationId: e.target.value })}
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-900 dark:text-white font-bold outline-none cursor-pointer"
                    >
                      {EVENT_STATIONS.map(st => (
                        <option key={st.id} value={st.id}>{st.stationNumber} ({st.name})</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Assigned Event Name *</label>
                    <input
                      type="text"
                      required
                      value={staffForm.assignedEvent}
                      onChange={e => setStaffForm({ ...staffForm, assignedEvent: e.target.value })}
                      placeholder="e.g. Annual Sports Day 2026"
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-900 dark:text-white font-bold outline-none focus:border-indigo-500"
                    />
                  </div>
                </div>

                {/* PIN Code & Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">4-Digit Kiosk PIN *</label>
                    <div className="relative">
                      <SafeIcon icon={FiKey} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        maxLength={6}
                        required
                        value={staffForm.pinCode}
                        onChange={e => setStaffForm({ ...staffForm, pinCode: e.target.value })}
                        placeholder="e.g. 1101"
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl pl-8 pr-3 py-2 text-slate-900 dark:text-white font-mono font-bold outline-none focus:border-indigo-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Contact Phone (Optional)</label>
                    <input
                      type="text"
                      value={staffForm.phone}
                      onChange={e => setStaffForm({ ...staffForm, phone: e.target.value })}
                      placeholder="+91 98765 43210"
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-900 dark:text-white font-bold outline-none focus:border-indigo-500"
                    />
                  </div>
                </div>

                {/* Booth Location & Duty Status */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Booth Location</label>
                    <input
                      type="text"
                      value={staffForm.boothLocation}
                      onChange={e => setStaffForm({ ...staffForm, boothLocation: e.target.value })}
                      placeholder="e.g. East Lawn Pavilion Booth #2"
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-900 dark:text-white font-bold outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Duty Status</label>
                    <select
                      value={staffForm.status}
                      onChange={e => setStaffForm({ ...staffForm, status: e.target.value })}
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-slate-900 dark:text-white font-bold outline-none cursor-pointer"
                    >
                      <option value="on_duty">🟢 On Duty</option>
                      <option value="break">🟡 On Break</option>
                      <option value="off_duty">⚪ Off Duty</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-200 dark:border-slate-800">
                  <button
                    type="button"
                    onClick={() => setIsStaffModalOpen(false)}
                    className="px-4 py-2 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 font-bold transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-black shadow-md transition-all cursor-pointer"
                  >
                    {editingStaffId ? 'Update Staff Member' : 'Save & Assign Staff'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default StationRoutingManager;
